import * as React from 'react';
import styled from 'styled-components';
import {
  ThemeProvider,
  SYS_BREAKPOINT_LG,
  SYS_BREAKPOINT_MD,
  SYS_BREAKPOINT_XL,
  SYS_BREAKPOINT_SM,
  SYS_BREAKPOINT_XS,
  SYS_BREAKPOINT_2_XL,
  SYS_COLOR_FOREGROUND_NEUTRAL_200_LIGHT,
  SYS_COLOR_FOREGROUND_NEUTRAL_200_DARK,
} from '@oneaudi/unified-web-common';
import type { UnifiedThemeType } from '@oneaudi/unified-web-common';
import {
  Text,
  Headline,
  ButtonGroup,
  TextButton,
  Layout,
  TextLink,
} from '@oneaudi/unified-web-components';

import {
  renderTextWithFootnotesReferencesV2,
  useContent,
  handleOpenInLayer,
  type AcsResult,
  type GfaServiceConfigLike,
} from '@oneaudi/feature-app-utils';
import { UeContainer, UeElement, UeReference } from '@oneaudi/falcon-tools';
import type { OneGraphServiceV1 } from '@oneaudi/onegraph-service';
import type { Logger } from '@feature-hub/logger';
import {
  ThemeColor,
  type BasicTeaserContent,
  type BasicTeaserCtaButton,
  type BasicTeaserServices,
  type FeatureAppMeta,
} from '../../types';
import { LegalInfo } from './LegalInfo';
import { isDebugMode } from '../utils';
import { APP_ID, APP_VERSION } from '../../environment';
import type { EditorJsonContent, FalconContent } from '../contentMapping/contentMapping';
import mapContent from '../contentMapping/contentMapping';
import { Image } from './Image';
import { Video } from './Video';
import {
  TrackingContextProvider,
  useImpressionTrackingRef,
  useTrackClick,
} from '../context/TrackingContext';
import { subtractOnePixel } from '../utils/subtractOnePixel';
import { RichText } from './RichText';
import { getBackgroundColor, getThemeName } from '../utils/alpha-helper';
import { NavigationConfirmation } from './NavigationConfirmation';
import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import { useI18n } from '@oneaudi/i18n-context';
import * as process from 'process';
import { CaeGate } from './CaeGate';

interface StyledContainerProps {
  mediaPosition?: BasicTeaserContent['asset']['mediaPosition'];
  legalInfoDisplay?: boolean;
  themeColor?: UnifiedThemeType['name'];
}

const StyledContainer = styled.div<StyledContainerProps>`
  img {
    color: ${({ themeColor }) =>
      themeColor === ThemeColor.Light
        ? SYS_COLOR_FOREGROUND_NEUTRAL_200_LIGHT
        : SYS_COLOR_FOREGROUND_NEUTRAL_200_DARK};
  }
  margin: auto;
  max-width: var(--max-content-width);
  background-color: ${({ themeColor }) => getBackgroundColor(themeColor)};
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  grid-template-areas:
    'media'
    'textarea';

  padding: var(--spacing-relative-xl) var(--spacing-relative-md);
  .image-disclaimer {
    margin-block-start: var(--spacing-relative-sm);
  }
  @media (min-width: ${SYS_BREAKPOINT_SM}) {
    padding-inline: var(--page-margin);
  }

  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    padding-inline: var(--page-margin);
  }

  @media (min-width: ${SYS_BREAKPOINT_LG}) {
    ${({ mediaPosition }: StyledContainerProps) =>
      mediaPosition === 'right'
        ? `
    grid-template-columns: 1fr 1fr;
    grid-template-areas: "textarea media";
    `
        : `
    grid-template-columns: 1fr 1fr;
    grid-template-areas: "media textarea";
    `};
    padding-inline: var(--spacing-relative-3xl);
    padding-block-end: ${({ legalInfoDisplay }: StyledContainerProps) =>
      !legalInfoDisplay ? `var(--spacing-relative-2xl)` : ''};
  }

  @media (min-width: ${SYS_BREAKPOINT_XL}) {
    ${({ mediaPosition }: StyledContainerProps) =>
      mediaPosition === 'right'
        ? `
    grid-template-columns: 1fr 1fr;
    grid-template-areas: "textarea media";
    `
        : `
    grid-template-columns: 1fr 1fr;
    grid-template-areas: "media textarea";
    `};
    padding-inline: var(--page-margin);
    padding-block-end: ${({ legalInfoDisplay }: StyledContainerProps) =>
      !legalInfoDisplay ? `var(--spacing-relative-2xl)` : ''};
  }
`;

interface StyledMediaProps {
  aspectRatio?: BasicTeaserContent['asset']['aspectRatio'];
  mediaPosition?: BasicTeaserContent['asset']['mediaPosition'];
}

const StyledMedia = styled.div<StyledMediaProps>`
  grid-area: media;
  position: relative;

  .div {
    @media screen and (min-width: ${SYS_BREAKPOINT_LG}) {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
    }
  }

  img {
    display: none;
    width: 100%;
    height: 100%;
    object-fit: cover;

    &.screen-size-xs {
      @media screen and (min-width: ${SYS_BREAKPOINT_XS}) and (max-width: ${subtractOnePixel(
          SYS_BREAKPOINT_SM,
        )}) {
        display: block;
      }
    }

    &.screen-size-s {
      @media screen and (min-width: ${SYS_BREAKPOINT_SM}) and (max-width: ${subtractOnePixel(
          SYS_BREAKPOINT_MD,
        )}) {
        display: block;
      }
    }

    &.screen-size-m {
      @media screen and (min-width: ${SYS_BREAKPOINT_MD}) and (max-width: ${subtractOnePixel(
          SYS_BREAKPOINT_LG,
        )}) {
        display: block;
      }
    }

    &.screen-size-l {
      @media screen and (min-width: ${SYS_BREAKPOINT_LG}) and (max-width: ${subtractOnePixel(
          SYS_BREAKPOINT_XL,
        )}) {
        display: block;
      }
    }

    &.screen-size-xl {
      @media screen and (min-width: ${SYS_BREAKPOINT_XL}) and (max-width: ${subtractOnePixel(
          SYS_BREAKPOINT_2_XL,
        )}) {
        display: block;
      }
    }

    &.screen-size-xxl {
      @media screen and (min-width: ${SYS_BREAKPOINT_2_XL}) {
        display: block;
      }
    }
  }
`;

const StyledImage = styled(StyledMedia)`
  height: 0;
  overflow: hidden;
  padding-top: ${({ aspectRatio }) => (aspectRatio === '1:1' ? '100' : '56.25')}%;

  .image__container {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    height: 100%;
  }
`;

const StyledTextArea = styled.div`
  sup {
    position: initial;
    line-height: initial;
  }

  grid-area: textarea;
  align-self: center;

  @media screen and (min-width: ${SYS_BREAKPOINT_XS}) {
    padding-block-start: var(--spacing-relative-lg);
  }
  @media screen and (min-width: ${SYS_BREAKPOINT_MD}) {
    a,
    button {
      width: auto;
    }
  }

  @media screen and (min-width: ${SYS_BREAKPOINT_LG}) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding-inline-start: var(--spacing-relative-2xl);
    padding-inline-end: var(--spacing-relative-2xl);
    padding-block-start: 0;
  }
  .button-group {
    /* Targets all direct children (buttons) */
    flex: 1; /* Make all buttons take equal space */
    min-width: 80px; /* Prevents buttons from being too small */
    text-align: center;
    flex-flow: wrap;
  }

  .subheading {
    margin-block-start: var(--spacing-relative-xs);
  }
  .text {
    margin-block-start: var(--spacing-relative-xs);
  }
  .buttons {
    margin-block-start: var(--spacing-relative-lg);
  }
  .supplier-info {
    white-space: nowrap; /* Prevents text from wrapping */
    margin-block-start: var(--spacing-relative-xl);
  }
  .supplier-info span {
    margin-inline-end: var(--spacing-relative-md);
  }
  > div:first-child {
    margin-block-start: 0;
  }
`;

export const BasicTeaser = ({
  themeColor,
  heading,
  headingTag,
  subHeading,
  text,
  buttons,
  consumptionAndEmissions,
  disclaimers,
  supplier,
  oneGraphService,
  loggerService,
  localeService,
  meta,
  asset,
  trackingService,
  layerManager,
}: BasicTeaserContent &
  BasicTeaserServices & {
    meta: FeatureAppMeta;
  }): React.ReactElement | null => {
  const impressionTrackingRef = useImpressionTrackingRef();
  const trackClick = useTrackClick();

  const confirmationTranslations = {
    confirmationHeadline: useI18n({
      id: 'nemo.ui.sc.externallink-confirm.headline',
      defaultMessage: '',
    }),

    confirmationCopy: useI18n({ id: 'nemo.ui.sc.externallink-confirm.copy', defaultMessage: '' }),

    confirmationCtaLabel: useI18n({
      id: 'nemo.ui.sc.externallink-confirm.cta-copy',
      defaultMessage: '',
    }),

    confirmationCtaAriaLabel: useI18n({
      id: 'nemo.ui.sc.externallink-confirm.cta-aria-label',
      defaultMessage: '',
    }),
  };

  const onClickWithConfirmation = (actionOnConfirm: () => void) => {
    const isAlpha = !process?.env?.IS_LEGACY;

    const handle = layerManager?.openFocusLayer(
      () => {
        return (
          <ThemeProvider theme={{ name: isAlpha ? 'dark' : 'light' }}>
            <NavigationConfirmation
              translations={confirmationTranslations}
              actionOnConfirm={() => {
                actionOnConfirm();
                handle?.close();
              }}
            />
          </ThemeProvider>
        );
      },
      {},
      {
        size: FocusLayerSizeV2.B,
        userCloseable: true,
      },
    );
  };

  if (trackingService) {
    trackingService.updateComponent({
      attributes: {
        implementer: 37,
        version: APP_VERSION,
        imageSize: asset.aspectRatio,
        ceDataAvailable: (consumptionAndEmissions?.length ?? 0) > 0,
      },
    });
  }

  const hasLegalInfo = !!consumptionAndEmissions?.length || !!disclaimers?.length;
  const { aspectRatio = '1:1', mediaPosition = 'left' } = asset;
  const subHeadingTag =
    // SEO optimization: subHeading tag is one hierarchy level lower than heading
    (headingTag ? `h${parseInt(headingTag.replace(/\D/g, '') || '2', 10) + 1}` : 'h3') as
      | 'h3'
      | 'h4'
      | 'h5';
  const legalInfo = hasLegalInfo ? (
    <LegalInfo
      mediaPosition={mediaPosition}
      consumptionAndEmissions={consumptionAndEmissions}
      disclaimers={disclaimers}
      oneGraphService={oneGraphService}
      loggerService={loggerService}
      localeService={localeService}
    />
  ) : null;

  const Buttons = () => {
    return buttons?.map(
      (
        {
          variant = 'primary',
          label,
          url,
          ariaLabel,
          openInTabOrLayer,
          chatBotId,
          needsConfirmation,
        },
        i,
      ) => {
        if (label && openInTabOrLayer === 'chatbot') {
          return (
            <TextButton
              variant={i === 0 ? variant : 'secondary'}
              key={label}
              aria-label={ariaLabel}
              size={process.env.IS_LEGACY ? 'md' : 'sm'}
              className="button"
              onClick={() => {
                trackClick?.({
                  pos: i + 1,
                  targetURL: url,
                  label,
                  action: 'content',
                  eventNameSuffix: 'open chatbot',
                  elementName: 'button',
                });
                window.acquireIO.max('text', chatBotId, null, false, 'bot');
              }}
            >
              <UeElement
                type="text"
                property={i === 0 ? 'firstButtonLabel' : 'secondButtonLabel'}
                label="Label"
              >
                {label}
              </UeElement>
            </TextButton>
          );
        }
        if (label && url) {
          const openInLayer = openInTabOrLayer === 'layer';
          const openInTab = openInTabOrLayer === 'tab';
          const hasLayerHash = url.includes('#oneLayer=');
          return (
            <TextButton
              variant={i === 0 ? variant : 'secondary'}
              key={label}
              aria-label={ariaLabel}
              className="button"
              size={process.env.IS_LEGACY ? 'md' : 'sm'}
              href={url}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                const textLinkUrl = e.currentTarget?.href;
                trackClick?.({
                  clickID: openInLayer || hasLayerHash ? 'layer-content' : '',
                  pos: i + 1,
                  targetURL: openInLayer
                    ? `${window.location.href}#one-layer=${url}`
                    : hasLayerHash
                      ? `${window.location.href}${url}`
                      : url,
                  label,
                  action: openInLayer || hasLayerHash ? 'content' : undefined,
                  eventNameSuffix: openInLayer || hasLayerHash ? 'layer open' : undefined,
                  elementName: 'button',
                });

                if (openInTab && textLinkUrl) {
                  e.preventDefault();
                  if (needsConfirmation) {
                    onClickWithConfirmation(() =>
                      window.open(textLinkUrl, '_blank', 'noopener,noreferrer'),
                    );
                  } else {
                    window.open(textLinkUrl, '_blank', 'noopener,noreferrer');
                  }
                }

                if (openInLayer) {
                  e.preventDefault();
                  handleOpenInLayer(url, false, true);
                }
              }}
            >
              <UeElement
                type="text"
                property={i === 0 ? 'firstButtonLabel' : 'secondButtonLabel'}
                label="Label"
              >
                {label}
              </UeElement>
            </TextButton>
          );
        }
        return null;
      },
    );
  };
  const shouldRenderButtons = () => {
    const isValidButton = (button: BasicTeaserCtaButton) =>
      button?.label && (button?.url || button?.openInTabOrLayer === 'chatbot');

    return buttons && buttons.some(isValidButton);
  };

  const overwrittenThemeColor = getThemeName(themeColor);

  return (
    <ThemeProvider theme={{ name: overwrittenThemeColor }}>
      <StyledContainer
        ref={impressionTrackingRef}
        themeColor={overwrittenThemeColor}
        mediaPosition={mediaPosition}
        data-testid="basic-teaser"
        legalInfoDisplay={hasLegalInfo}
      >
        {asset.assetType === 'image' && (
          <UeContainer propertyPath="asset" label="images">
            <UeReference propertyPath="asset" behavior="component">
              {(ueProps) => (
                <>
                  <StyledImage aspectRatio={aspectRatio} mediaPosition={mediaPosition} {...ueProps}>
                    <Image asset={asset} />
                  </StyledImage>
                  {asset.assetDisclaimers?.map((disclaimer) => (
                    <Text
                      key={`image-disclaimer-${Math.floor(Math.random() * 100)}`}
                      className="image-disclaimer"
                      variant={{
                        type: 'copy',
                        order: process.env.IS_LEGACY ? '2' : '3',
                        style: 'normal',
                      }}
                    >
                      {renderTextWithFootnotesReferencesV2(disclaimer)}
                    </Text>
                  ))}
                </>
              )}
            </UeReference>
          </UeContainer>
        )}
        {asset.assetType === 'video' && (
          <UeContainer propertyPath="asset" label="videos">
            <UeReference propertyPath="asset" behavior="component">
              {(ueProps) => (
                <StyledMedia aspectRatio={aspectRatio} mediaPosition={mediaPosition} {...ueProps}>
                  <UeElement type="media" property="assetSrc" label="video">
                    <Video asset={asset} meta={meta} />
                  </UeElement>
                  {asset.assetDisclaimers?.map((disclaimer) => (
                    <Text
                      key={`video-disclaimer-${Math.floor(Math.random() * 100)}`}
                      className="image-disclaimer"
                      variant={{
                        type: 'copy',
                        order: process.env.IS_LEGACY ? '2' : '3',
                        style: 'normal',
                      }}
                    >
                      {renderTextWithFootnotesReferencesV2(disclaimer)}
                    </Text>
                  ))}
                </StyledMedia>
              )}
            </UeReference>
          </UeContainer>
        )}

        <StyledTextArea>
          {heading && (
            <div>
              <UeElement type="text" property="heading" label="Headline">
                {(ueProps) => (
                  <Headline
                    tag={headingTag || 'h2'}
                    {...ueProps}
                    variant={{
                      order: '2',
                      type: 'headline',
                      weight: 'normal',
                    }}
                  >
                    {renderTextWithFootnotesReferencesV2(heading)}
                  </Headline>
                )}
              </UeElement>
            </div>
          )}
          {subHeading && (
            <div className="subheading">
              <UeElement type="text" property="subHeading" label="Subline">
                {(ueProps) => (
                  <Headline
                    tag={subHeadingTag}
                    {...ueProps}
                    variant={{
                      order: '3',
                      type: 'headline',
                      weight: 'normal',
                    }}
                  >
                    {renderTextWithFootnotesReferencesV2(subHeading)}
                  </Headline>
                )}
              </UeElement>
            </div>
          )}
          {text && (
            <div>
              <UeElement type="text" property="text" label="Copy">
                {(ueProps) => (
                  <RichText trackingService={trackingService} text={text} {...ueProps} />
                )}
              </UeElement>
            </div>
          )}
          {shouldRenderButtons() && (
            <div className="buttons">
              {/* @ts-expect-error  Buttons does produce  multiple children */}
              <ButtonGroup mode="fill" className="button-group" size="md">
                {Buttons()}
              </ButtonGroup>
            </div>
          )}
          {hasLegalInfo && legalInfo}
          {supplier?.supplierName && supplier?.supplierLink.label && supplier?.supplierLink.url && (
            <Layout className="supplier-info">
              <UeElement type="text" property="supplierName" label="Supplier Name">
                {(ueProps) => (
                  <Text
                    tag="span"
                    variant={{
                      type: 'copy',
                      order: '2',
                      style: 'normal',
                    }}
                    {...ueProps}
                  >
                    {supplier.supplierName}
                  </Text>
                )}
              </UeElement>
              <UeElement type="text" property="supplierLinkLabel" label="Supplier Name">
                {(ueProps) => (
                  <TextLink
                    href={supplier.supplierLink.url}
                    // aria-label={supplier.supplierLink.ariaLabel}
                    target="_blank"
                    rel="noreferrer"
                    size="sm"
                    variant="inline"
                    {...ueProps}
                  >
                    {supplier.supplierLink.label}
                  </TextLink>
                )}
              </UeElement>
            </Layout>
          )}
        </StyledTextArea>
      </StyledContainer>
    </ThemeProvider>
  );
};

export const BasicTeaserContentLoader = ({
  initialContent,
  localeService,
  loggerService,
  oneGraphService,
  trackingService,
  meta,
  layerManager,
  acsResult,
  gfaServiceConfigProvider,
}: {
  meta: FeatureAppMeta;
  initialContent?: BasicTeaserContent;
  loggerService?: Logger;
  oneGraphService: OneGraphServiceV1;
  acsResult?: AcsResult;
  gfaServiceConfigProvider?: GfaServiceConfigLike;
} & BasicTeaserServices): React.ReactElement | null => {
  const rawContent = useContent<BasicTeaserContent>() || initialContent;
  const content = mapContent(rawContent as EditorJsonContent | FalconContent);

  if (isDebugMode())
    console.debug(`${APP_ID} ->`, {
      content,
    });

  if (!content) return null;

  return (
    <CaeGate
      content={content}
      localeService={localeService}
      oneGraphService={oneGraphService}
      loggerService={loggerService}
      acsResult={acsResult}
      gfaServiceConfigProvider={gfaServiceConfigProvider}
    >
      <TrackingContextProvider trackingService={trackingService} heading={content.heading}>
        <BasicTeaser
          {...content}
          localeService={localeService}
          trackingService={trackingService}
          loggerService={loggerService}
          oneGraphService={oneGraphService}
          meta={meta}
          layerManager={layerManager}
        />
      </TrackingContextProvider>
    </CaeGate>
  );
};
